/**
 * Stripe.js(Payment Element)のadapter境界(Phase「LIFF frontend実装」)。
 *
 * 正本(実装指示書 v2.0 13章): PaymentIntent + Stripe.js / Payment Element。
 * Stripe Secret Keyはbackendのみが保持し、このfrontendにはpublishable key
 * (公開鍵、pk_...)しか渡らない。Stripe.jsはCDN(https://js.stripe.com/v3/、
 * index.html)からのみ読み込み、npmバンドル・自己ホストは行わない
 * (Stripe公式のPCI DSS適合方針)。
 *
 * `window.Stripe`はCDN scriptが定義するグローバルであり、単体テスト環境
 * (jsdom)には存在しないため、interfaceの背後へ隔離してFakeで置き換え可能に
 * する。
 */

export interface PaymentElementMountResult {
  /** 決済確定を行う。成功時はresolve、失敗時はエラーメッセージをrejectする。 */
  confirmPayment(): Promise<{ ok: true } | { ok: false; message: string }>;
}

export interface StripeClient {
  mountPaymentElement(clientSecret: string, containerId: string): Promise<PaymentElementMountResult>;
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Stripe?: (publishableKey: string) => any;
  }
}

export class StripeNotLoadedError extends Error {
  constructor() {
    super("Stripe.js is not loaded (window.Stripe is undefined). Check index.html's <script src=\"https://js.stripe.com/v3/\">.");
    this.name = "StripeNotLoadedError";
  }
}

export class RealStripeClient implements StripeClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly stripe: any;

  constructor(publishableKey: string) {
    if (typeof window === "undefined" || typeof window.Stripe !== "function") {
      throw new StripeNotLoadedError();
    }
    this.stripe = window.Stripe(publishableKey);
  }

  async mountPaymentElement(clientSecret: string, containerId: string): Promise<PaymentElementMountResult> {
    const elements = this.stripe.elements({ clientSecret });
    const paymentElement = elements.create("payment");
    paymentElement.mount(`#${containerId}`);

    return {
      confirmPayment: async () => {
        const result = await this.stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });
        if (result.error) {
          return { ok: false, message: result.error.message ?? "決済に失敗しました" };
        }
        return { ok: true };
      },
    };
  }
}
